import React from "react";
import { Link } from "react-router-dom";
import { 
  Github, 
  Linkedin, 
  Facebook, 
  Instagram,
  Mail,
  Phone,
  Code,
  ExternalLink,
  ArrowUp
} from "lucide-react";
import { Button } from "./ui/button";

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <footer className="bg-zinc-50 dark:bg-zinc-900 border-t border-zinc-200 dark:border-zinc-800">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Top section with back to top button */}
        <div className="py-8 flex justify-center">
          <Button
            onClick={scrollToTop}
            variant="outline"
            size="sm"
            className="rounded-full group hover:bg-blue-50 dark:hover:bg-zinc-800 border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-400 hover:text-blue-600 dark:hover:text-blue-400"
          >
            <ArrowUp size={16} className="mr-2 group-hover:-translate-y-0.5 transition-transform" />
            Back to top
          </Button>
        </div>

        {/* Main footer content */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 py-10 border-t border-zinc-200 dark:border-zinc-800">
          {/* Personal Info */}
          <div>
            <div className="flex items-center gap-2 mb-5">
              <div className="bg-gradient-to-r from-blue-600 to-violet-600 text-white p-1.5 rounded">
                <Code size={18} />
              </div>
              <h3 className="font-bold text-zinc-900 dark:text-white">Casper De Bock</h3>
            </div>
            <address className="not-italic space-y-2 text-zinc-600 dark:text-zinc-400 text-sm">
              <p>Vaddenhoek 8</p>
              <p>9772 Wannegem-Lede</p>
              <p>Belgium</p>
            </address>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-zinc-900 dark:text-white mb-5">
              Contact
            </h3>
            <div className="space-y-3">
              <a href="tel:+32468171264" className="flex items-center gap-2 text-zinc-600 hover:text-blue-600 dark:text-zinc-400 dark:hover:text-blue-400 transition-colors text-sm">
                <Phone size={16} />
                +32 468 17 12 64
              </a>
              <a href="mailto:casper.debock@gmail.com" className="flex items-center gap-2 text-zinc-600 hover:text-blue-600 dark:text-zinc-400 dark:hover:text-blue-400 transition-colors text-sm">
                <Mail size={16} />
                casper.debock@gmail.com
              </a>
            </div>
            
            
          </div>

          {/* Social Links */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-zinc-900 dark:text-white mb-5">
              Follow Me
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <a 
                href="https://github.com/CasperDeBock" 
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-2 text-zinc-600 hover:text-blue-600 dark:text-zinc-400 dark:hover:text-blue-400 transition-colors text-sm group"
              >
                <div className="p-1.5 bg-zinc-100 dark:bg-zinc-800 rounded-md group-hover:bg-blue-50 dark:group-hover:bg-zinc-700 transition-colors">
                  <Github size={16} />
                </div>
                <span>Github</span>
                <ExternalLink size={14} className="ml-auto opacity-50" />
              </a>
              
              <a 
                href="https://www.linkedin.com/in/casperdebock/" 
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-2 text-zinc-600 hover:text-blue-600 dark:text-zinc-400 dark:hover:text-blue-400 transition-colors text-sm group"
              >
                <div className="p-1.5 bg-zinc-100 dark:bg-zinc-800 rounded-md group-hover:bg-blue-50 dark:group-hover:bg-zinc-700 transition-colors">
                  <Linkedin size={16} />
                </div>
                <span>LinkedIn</span>
                <ExternalLink size={14} className="ml-auto opacity-50" />
              </a>
              
              <a 
                href="https://www.facebook.com/casper.debock/" 
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-2 text-zinc-600 hover:text-blue-600 dark:text-zinc-400 dark:hover:text-blue-400 transition-colors text-sm group"
              >
                <div className="p-1.5 bg-zinc-100 dark:bg-zinc-800 rounded-md group-hover:bg-blue-50 dark:group-hover:bg-zinc-700 transition-colors">
                  <Facebook size={16} />
                </div>
                <span>Facebook</span>
                <ExternalLink size={14} className="ml-auto opacity-50" />
              </a>
              
              <a 
                href="https://www.instagram.com/casperdebock" 
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-2 text-zinc-600 hover:text-blue-600 dark:text-zinc-400 dark:hover:text-blue-400 transition-colors text-sm group"
              >
                <div className="p-1.5 bg-zinc-100 dark:bg-zinc-800 rounded-md group-hover:bg-blue-50 dark:group-hover:bg-zinc-700 transition-colors">
                  <Instagram size={16} />
                </div>
                <span>Instagram</span>
                <ExternalLink size={14} className="ml-auto opacity-50" />
              </a>
            </div>
          </div>
        </div>

        {/* Bottom copyright and links */}
        <div className="py-6 border-t border-zinc-200 dark:border-zinc-800 flex flex-col md:flex-row justify-between items-center text-sm text-zinc-500 dark:text-zinc-500">
          <div className="mb-4 md:mb-0">
            © {new Date().getFullYear()} Casper De Bock. All rights reserved.
          </div>
          <div className="flex space-x-6">
            <Link to="/projects" className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
              Projects
            </Link>
            <Link to="/contact" className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
              Contact
            </Link>
            <a href="#" className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
              Privacy Policy
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;